import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParentPortal } from './ParentPortalContext';
import { supabase } from '../../../../lib/supabase';

const ParentAssistanceContext = createContext();

export const useParentAssistance = () => {
  const context = useContext(ParentAssistanceContext);
  if (!context) {
    throw new Error('useParentAssistance must be used within a ParentAssistanceProvider');
  }
  return context;
};

export const ParentAssistanceProvider = ({ children }) => {
  const { selectedStudent, studentProgress, studentJourneyPhase } = useParentPortal();

  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [contextualHelp, setContextualHelp] = useState(null);
  const [waitStateContent, setWaitStateContent] = useState(null);

  // Generate smart suggestions based on student progress
  const generateSmartSuggestions = (student, progress, phase) => {
    if (!student || !progress) return [];

    const suggestions = [];

    // College suggestions based on phase
    if (phase === 'discovery' && !progress.hasAssessment) {
      suggestions.push({
        id: 'complete-assessment',
        type: 'strategic',
        priority: 'high',
        title: 'Strategic Assessment Ready',
        description: `Help ${student.name} discover their unique strengths and positioning`,
        action: 'Start Parent Observations',
        route: '/college/parent/assessment',
        icon: 'Brain',
        reasoning: 'Early strategic clarity leads to better college fit and stronger applications'
      });
    }

    if (phase === 'planning' && progress.colleges.length < 3) {
      suggestions.push({
        id: 'suggest-colleges',
        type: 'colleges',
        priority: 'medium',
        title: 'College Research Opportunity',
        description: `${student.name} could benefit from strategic college suggestions`,
        action: 'Add Strategic Colleges',
        route: '/college/parent/colleges',
        icon: 'GraduationCap',
        reasoning: 'Diverse college lists increase admission chances and scholarship opportunities'
      });
    }

    if (phase === 'execution' && progress.essays.length === 0) {
      suggestions.push({
        id: 'essay-support',
        type: 'collaboration',
        priority: 'high',
        title: 'Essay Support Ready',
        description: 'Applications season - your feedback will be crucial',
        action: 'Review Shared Essays',
        route: '/college/parent/shared',
        icon: 'FileText',
        reasoning: 'Parent feedback during essay development significantly improves outcomes'
      });
    }

    // Timeline-based suggestions
    const currentMonth = new Date().getMonth();
    if (currentMonth >= 8 && currentMonth <= 11 && phase === 'execution') { // Sept-Dec
      suggestions.push({
        id: 'deadline-focus',
        type: 'timeline',
        priority: 'urgent',
        title: 'Application Season Active',
        description: 'Monitor deadlines and provide strategic support',
        action: 'View Timeline',
        route: '/college/parent/timeline',
        icon: 'Calendar',
        reasoning: 'This is the critical period where parent support has maximum impact'
      });
    }

    return suggestions;
  };

  // Generate quick actions based on context
  const generateQuickActions = (student, progress, phase) => {
    if (!student) return [];

    const actions = [];

    // Always available quick actions
    actions.push({
      id: 'quick-feedback',
      label: 'Quick Feedback',
      description: 'Add encouragement to shared work',
      icon: 'MessageCircle',
      onClick: () => {
        // Navigate to shared documents with feedback mode
      }
    });

    // Phase-specific actions
    if (phase === 'planning') {
      actions.push({
        id: 'suggest-college',
        label: 'Suggest College',
        description: 'Add a strategic college recommendation',
        icon: 'Plus',
        onClick: () => {
          // Open college suggestion modal
        }
      });
    }

    if (phase === 'execution') {
      actions.push({
        id: 'check-deadlines',
        label: 'Check Deadlines',
        description: 'Review upcoming application deadlines',
        icon: 'Clock',
        onClick: () => {
          // Navigate to timeline with deadline filter
        }
      });
    }

    return actions;
  };

  // Get contextual help based on current location and phase
  const getContextualHelp = (route, phase) => {
    const helpContent = {
      overview: {
        discovery: {
          title: 'Supporting Early Exploration',
          content: 'Your role now is to help your student discover their strengths and interests. The strategic assessment will provide valuable insights.',
          tips: ['Encourage self-reflection', 'Avoid pressure about specific schools', 'Focus on strengths identification']
        },
        planning: {
          title: 'Strategic Planning Phase',
          content: 'Now you can actively help with college research and strategic positioning.',
          tips: ['Research colleges together', 'Discuss fit factors', 'Consider financial aspects']
        },
        execution: {
          title: 'Application Support',
          content: 'This is when your strategic input and emotional support are most valuable.',
          tips: ['Monitor deadlines', 'Provide essay feedback', 'Manage stress levels']
        }
      },
      shared: {
        discovery: {
          title: 'Early Document Sharing',
          content: 'Students may share preliminary thoughts or assessment results for your input.',
          tips: ['Ask clarifying questions', 'Celebrate insights', 'Avoid editing directly']
        },
        planning: {
          title: 'Collaborative Review',
          content: 'Review shared essays, brag sheets, and college lists with strategic perspective.',
          tips: ['Focus on authentic voice', 'Suggest strategic positioning', 'Maintain student ownership']
        },
        execution: {
          title: 'Critical Feedback Period',
          content: 'Your feedback on application materials can significantly impact outcomes.',
          tips: ['Be timely with feedback', 'Balance encouragement with suggestions', 'Respect final decisions']
        }
      }
    };

    return helpContent[route]?.[phase] || null;
  };

  // Get productive wait state content
  const getWaitStateContent = (context) => {
    const waitContent = {
      loading_progress: [
        'Strategic insight: Students who involve parents in college planning are 40% more likely to feel confident about their choices.',
        'Research shows: Parent feedback during essay development leads to stronger, more authentic personal statements.',
        'Did you know? Families who plan strategically often discover unexpected scholarship opportunities.',
      ],
      loading_shared: [
        'Writing tip: The most compelling essays often start with a moment of uncertainty or growth, not achievement.',
        'Strategic advantage: Essays that connect personal values to college missions create stronger impressions.',
        'Insight: Admission officers can spot "parent-written" content immediately - authentic student voice is crucial.',
      ],
      loading_colleges: [
        'Lesser-known fact: Students admitted to their "match" schools often report higher satisfaction than those at reach schools.',
        'Strategic tip: Geographic diversity can significantly impact admission chances at many colleges.',
        'Research insight: Demonstrated interest can be the tiebreaker between similar applicants.',
      ]
    };

    const content = waitContent[context];
    if (content) {
      return content[Math.floor(Math.random() * content.length)];
    }
    return null;
  };

  // Update suggestions when context changes
  useEffect(() => {
    if (selectedStudent && studentProgress) {
      const suggestions = generateSmartSuggestions(selectedStudent, studentProgress, studentJourneyPhase);
      const actions = generateQuickActions(selectedStudent, studentProgress, studentJourneyPhase);

      setSmartSuggestions(suggestions);
      setQuickActions(actions);
    }
  }, [selectedStudent, studentProgress, studentJourneyPhase]);

  const contextValue = {
    // AI-powered assistance
    smartSuggestions,
    quickActions,
    contextualHelp,
    waitStateContent,

    // Methods
    generateSmartSuggestions,
    getContextualHelp,
    getWaitStateContent,

    // Utility functions
    setContextualHelp: (route, phase) => {
      setContextualHelp(getContextualHelp(route, phase));
    },

    showWaitContent: (context) => {
      setWaitStateContent(getWaitStateContent(context));
    },

    clearWaitContent: () => {
      setWaitStateContent(null);
    }
  };

  return (
    <ParentAssistanceContext.Provider value={contextValue}>
      {children}
    </ParentAssistanceContext.Provider>
  );
};